/* The authenticated client.
 *
 * The access token lives in memory, never in localStorage: a token readable
 * by JavaScript is a token an injected script can take. The refresh token is
 * an httpOnly cookie the browser sends on its own, so a reload recovers the
 * session without the page ever having held the long-lived credential.
 *
 * One refresh at a time: a page that fires six requests after the token
 * expires must not start six refreshes, five of which would then present an
 * already-rotated token and fail.
 */

export interface Me {
  email: string
  full_name: string
  roles: string[]
  plants: number[] | null
  permissions: string[]
  must_change_password: boolean
  status: string
}

export interface AuthMode {
  mode: 'none' | 'local'
  self_registration: boolean
  min_password_length: number
  roles: { id: string; description: string }[]
}

let accessToken: string | null = null
let refreshing: Promise<string | null> | null = null

export function setToken(token: string | null): void {
  accessToken = token
}

export function hasToken(): boolean {
  return accessToken !== null
}

async function refreshToken(): Promise<string | null> {
  // Collapse concurrent refreshes onto one in-flight request.
  if (!refreshing) {
    refreshing = fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) return null
        const body = (await response.json()) as { access_token: string }
        accessToken = body.access_token
        return accessToken
      })
      .catch(() => null)
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

/** The one place a request gains its credentials. */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = (token: string | null) =>
    fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

  let response = await send(accessToken)

  // One retry, and only on 401: an expired access token is the normal case
  // every 15 minutes, and the user must not see it.
  if (response.status === 401 && accessToken !== null) {
    const fresh = await refreshToken()
    if (fresh) response = await send(fresh)
  }
  return response
}

export const auth = {
  async mode(): Promise<AuthMode> {
    const response = await fetch('/api/auth/mode')
    return response.json() as Promise<AuthMode>
  },

  async login(email: string, password: string): Promise<Me> {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const body = await response.json()
    if (!response.ok) throw new Error(body.detail ?? 'sign-in failed')
    accessToken = body.access_token
    return body.user as Me
  },

  /** Recover a session on page load, from the refresh cookie alone. */
  async restore(): Promise<Me | null> {
    const token = await refreshToken()
    if (!token) return null
    const response = await authedFetch('/api/auth/me')
    if (!response.ok) return null
    return response.json() as Promise<Me>
  },

  async logout(): Promise<void> {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    accessToken = null
  },

  async register(
    email: string,
    fullName: string,
    password: string,
    requestedRoles: string[],
  ): Promise<{ status: string; detail: string }> {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        full_name: fullName,
        password,
        requested_roles: requestedRoles,
      }),
    })
    const body = await response.json()
    if (!response.ok) throw new Error(body.detail ?? 'registration failed')
    return body
  },

  async changePassword(current: string, next: string): Promise<void> {
    const response = await authedFetch('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: current, new_password: next }),
    })
    if (!response.ok) {
      const body = await response.json()
      throw new Error(body.detail ?? 'could not change the password')
    }
    accessToken = null // every session was revoked; sign in again
  },
}

/* Permission helpers.
 *
 * The UI hides what a role cannot do, but the API is what ENFORCES it: a
 * hidden button is a courtesy, not a control. Both exist so a planner is not
 * shown a Publish button they will be refused, while a crafted request is
 * still refused.
 */
export const can = (me: Me | null, permission: string): boolean =>
  me?.permissions.includes(permission) ?? false

export const PERMISSIONS = {
  dashboardRead: 'dashboard.read',
  reportsDownload: 'reports.download',
  simulationEdit: 'simulation.edit',
  scenarioCreate: 'scenario.create',
  planSubmit: 'plan.submit',
  planApprove: 'plan.approve',
  planPublish: 'plan.publish',
  planReopen: 'plan.reopen',
  masterdataWrite: 'masterdata.write',
  settingsWrite: 'settings.write',
  crewsWrite: 'crews.write',
  usersManage: 'users.manage',
} as const
